import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { tokenStorage } from '@/lib/auth/tokenStorage'
import { authEvents } from '@/lib/auth/authEvents'

interface AuthContextValue {
  isAuthenticated: boolean
  token: string | null
  vendorId: string | null
  login: (token: string, vendorId?: string) => void
  logout: () => void
  setVendorId: (vendorId: string) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() => tokenStorage.getToken())
  const [vendorId, setVendorIdState] = useState<string | null>(() => tokenStorage.getVendorId())
  const navigate = useNavigate()

  const logout = useCallback(() => {
    tokenStorage.clearAll()
    setToken(null)
    setVendorIdState(null)
    navigate('/login')
  }, [navigate])

  useEffect(() => {
    const unsubscribe = authEvents.on('unauthorized', logout)
    return unsubscribe
  }, [logout])

  const login = useCallback((newToken: string, newVendorId?: string) => {
    tokenStorage.setToken(newToken)
    setToken(newToken)
    if (newVendorId) {
      tokenStorage.setVendorId(newVendorId)
      setVendorIdState(newVendorId)
    }
  }, [])

  const setVendorId = useCallback((id: string) => {
    tokenStorage.setVendorId(id)
    setVendorIdState(id)
  }, [])

  const value: AuthContextValue = {
    isAuthenticated: !!token,
    token,
    vendorId,
    login,
    logout,
    setVendorId,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
